import {
  params,
  string,
  shape,
  bool,
  arrayOf,
  alias,
  number,
  customType
} from '@thd-nucleus/data-sources';

export const ProductDataModel = {
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    bundleSpecificationDetails: params({ storeId: string(), zipCode: string() }).shape({
      parents: arrayOf(shape({
        parentId: string()
      }))
    }),
    info: shape({
      recommendationFlags: shape({
        packages: bool(),
        frequentlyBoughtTogether: bool(),
        bundles: bool()
      })
    }),
    identifiers: shape({
      productType: string()
    }),
    availabilityType: shape({
      discontinued: bool(),
      type: string()
    }),
    fulfillment: params({ storeId: string() }).shape({
      backordered: bool(),
      fulfillmentOptions: arrayOf(
        shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(
            shape({
              type: string(),
              locations: arrayOf(
                shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                })
              )
            })
          )
        })
      )
    }),
    media: shape({
      image: shape({
        url: string()
      }).client()
    })
  })
};

const Service = shape({
  deliveryTimeline: string(),
  deliveryDates: shape({
    startDate: string(),
    endDate: string()
  }),
  deliveryCharge: string(),
  dynamicEta: shape({
    hours: string(),
    minutes: string()
  }),
  hasFreeShipping: bool(),
  deliveryMessage: string(),
  freeDeliveryThreshold: number({ float: true }),
  earliestDeliveryDate: string(),
  locations: arrayOf(shape({
    curbsidePickupFlag: bool(),
    isBuyInStoreCheckNearBy: bool(),
    distance: number({ float: true }),
    inventory: shape({
      isOutOfStock: bool(),
      isInStock: bool(),
      isLimitedQuantity: bool(),
      isUnavailable: bool(),
      quantity: number(),
      maxAllowedBopisQty: number(),
      minAllowedBopisQty: number()
    }),
    isAnchor: bool(),
    locationId: string(),
    state: string(),
    storeName: string(),
    storePhone: string(),
    type: string()
  })),
  shipFromFastestLocation: bool(),
  type: string(),
  totalCharge: number({ float: true }),
  optimalFulfillment: bool()
});

const promotionalAdjustmentFields = {
  type: string(),
  description: shape({
    shortDesc: string(),
    longDesc: string()
  }),
  dollarOff: number({ float: true }),
  percentageOff: number({ float: true }),
  promoId: string()
};

// itemQuantities is supported on promotionalAdjustments only (backend rejects it on
// bundlePromotionalAdjustments); it makes the backend compute each item's promos in full-bundle context.
const makePricing = ({ withItemQuantities = false, withBadgeFields = false } = {}) => params({
  storeId: string(), isBrandPricingPolicyCompliant: bool()
}).shape({
  original: number({ float: true }),
  // getProductPricing reads mapAboveOriginalPrice to decide MAP messaging; drop it and the
  // mini rows render MAP differently than product-bundle v9's bundle page.
  mapAboveOriginalPrice: bool(),
  mapDetail: shape({
    percentageOff: number({ float: true }),
    dollarOff: number({ float: true }),
    effectiveMap: number({ float: true }),
    mapPolicy: string(),
    mapOriginalPriceViolation: bool(),
    mapSpecialPriceViolation: bool()
  }),
  message: string(),
  promotion: shape({
    type: string(),
    description: shape({
      shortDesc: string(),
      longDesc: string()
    }),
    dollarOff: number({ float: true }),
    percentageOff: number({ float: true }),
    promotionTag: string(),
    experienceTag: string(),
    subExperienceTag: string(),
    // Special Buy / Savings Center badge promos render on per-item rows via getProductPricing.
    ...(withBadgeFields ? {
      savingsCenter: string(),
      savingsCenterPromos: string(),
      specialBuySavings: string(),
      specialBuyDollarOff: string(),
      specialBuyPercentageOff: string()
    } : {})
  }),
  promotionalAdjustments: withItemQuantities
    ? params({
      itemQuantities: arrayOf(customType('ItemQuantity').shape({
        itemId: string(),
        quantity: number()
      }))
    }).arrayOf(shape(promotionalAdjustmentFields))
    : arrayOf(shape(promotionalAdjustmentFields)),
  bundlePromotionalAdjustments: arrayOf(shape({
    dates: shape({
      start: string(),
      end: string()
    }),
    description: shape({
      shortDesc: string(),
      longDesc: string()
    }),
    dollarOff: number({ float: true }),
    percentageOff: number({ float: true }),
    promoId: string(),
    promotionTag: string(),
    promotionId: string()
  })),
  specialBuy: number({ float: true }),
  value: number({ float: true })
});

const Pricing = makePricing();
const PricingWithItemQuantities = makePricing({ withItemQuantities: true, withBadgeFields: true });

const loyaltyMembershipInput = customType('LoyaltyMembershipInput').shape({
  svocID: string(),
  programTiers: arrayOf(shape({
    tier: string(),
    program: string()
  }))
});

export const BundlesDataModel = {
  products: params({
    itemIds: arrayOf(string().isRequired()).isRequired(),
    loyaltyMembershipInput
  }).arrayOf(shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      productLabel: string(),
      brandName: string(),
      canonicalUrl: string(),
      hideItem: string()
    }),
    availabilityType: shape({
      discontinued: bool()
    }),
    fulfillment: params({ storeId: string(), zipCode: string() }).shape({
      anchorStoreStatus: bool(),
      anchorStoreStatusType: string(),
      backordered: bool(),
      backorderedShipDate: string(),
      bossExcludedShipStates: string(),
      sthExcludedShipState: string(),
      excludedShipStates: string(),
      seasonStatusEligible: bool(),
      fulfillmentOptions: arrayOf(shape({
        type: string(),
        fulfillable: bool(),
        services: arrayOf(Service),
        priorityDeliveryOptions: arrayOf(shape({
          date: string(),
          timeline: string(),
          totalCharge: number({ float: true })
        }))
      })),
      fulfillmentBundleMessage: string(),
      onlineStoreStatus: bool(),
      onlineStoreStatusType: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        sizes: arrayOf(string())
      }))
    }),
    bundleSpecificationDetails: shape({
      experienceType: string(),
      type: string(),
      brand: string(),
      components: arrayOf(shape({
        defaultProductId: string(),
        quantity: string(),
        type: string(),
        selectableVariants: arrayOf(shape({
          name: string(),
          id: string(),
          guid: string(),
          value: string()
        }))
      })),
      accessories: shape({
        categories: arrayOf(shape({
          categoryName: string(),
          offerings: arrayOf(shape({
            offeringName: string(),
            products: arrayOf(shape({
              defaultProductId: string(),
              type: string(),
              quantity: string()
            }))
          }))
        }))
      })
    }),
    pricing: Pricing,
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  }))
};

export const BundleProductsDataModel = {
  products: params({
    itemIds: arrayOf(string().isRequired()).isRequired(),
    dataSource: string(),
    loyaltyMembershipInput
  }).arrayOf({
    itemId: string(),
    dataSource: string(),
    identifiers: shape({
      applianceType: string(),
      brandName: string(),
      productLabel: string(),
      storeSkuNumber: string()
    }),
    pricing: PricingWithItemQuantities,
    fulfillment: params({ storeId: string() }).shape({
      backordered: bool(),
      fulfillmentOptions: arrayOf(
        shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(
            shape({
              type: string(),
              locations: arrayOf(
                shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                })
              )
            })
          )
        })
      )
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        sizes: arrayOf(string())
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
};

const ReviewProductDataStore = shape({
  FilteredReviewStatistics: shape({
    AverageOverallRating: string(),
    TotalReviewCount: number()
  })
});

export const BundleRating = {
  reviews: params({
    itemId: string().isRequired(),
    searchTerm: string(),
    sortBy: string(),
    startIndex: number(),
    filters: customType('ReviewsFilterInput')
      .shape({
        isVerifiedPurchase: bool(),
        prosCons: string(),
        starRatings: arrayOf(number())
      })
  }).shape({
    Includes: shape({
      Products: shape({
        store: ReviewProductDataStore,
        items: arrayOf(ReviewProductDataStore)
      })
    }),
    TotalResults: number()
  })
};